import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { awardService } from '../../services/awardService';
import { ArrowLeft, Trophy, Users, Calendar, ChevronLeft, ChevronRight, MapPin, Clock, Crown, Grid } from 'lucide-react';
import { ContestantRanking } from '../../components/contest/ContestantRanking';
import { fetchContest } from '../../services/contestService';
import { motion } from 'framer-motion';
import { AwardContestantBlocks } from '../../components/award/AwardContestantBlocks';
import { PublicHeader } from '../../components/layout/PublicHeader';
import { PublicFooter } from '../../components/layout/PublicFooter';
import { GoldSparkles } from '../../components/shared/GoldSparkles';
import { supabase } from '../../lib/supabase';
import { VoteModal } from '../../components/vote/VoteModal';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';

// Importation de la police Playfair Display
import '@fontsource/playfair-display/400.css';
import '@fontsource/playfair-display/500.css';
import '@fontsource/playfair-display/600.css';

export const AwardDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  // États pour suivre la catégorie active et l'affichage des catégories
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [showCategories, setShowCategories] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const { data: award, isLoading } = useQuery({
    queryKey: ['award', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('awards')
        .select(`
          *,
          award_categories (
            id,
            name
          )
        `)
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!slug
  });

  const { data: categories, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['award-contests', award?.id],
    queryFn: async () => {
      if (!award?.id) return [];
      const { data, error } = await supabase
        .from('award_categories')
        .select(`
          id,
          name,
          created_at,
          contest:contest_id (
            id,
            name,
            slug,
            description
          )
        `)
        .eq('award_id', award.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!award?.id
  });

  // Fonction pour sélectionner une catégorie aléatoire
  const selectRandomCategory = () => {
    if (categories && categories.length > 0) {
      const randomIndex = Math.floor(Math.random() * categories.length);
      setActiveCategory(categories[randomIndex].id);
    }
  };

  // Utiliser useEffect pour mettre à jour la catégorie active quand les catégories sont chargées
  useEffect(() => {
    if (categories && categories.length > 0 && !activeCategory) {
      selectRandomCategory();
    }
  }, [categories, activeCategory]);



  // Utiliser IntersectionObserver pour suivre les catégories visibles
  useEffect(() => {
    // Créer l'observateur pour tous les appareils
    observerRef.current = new IntersectionObserver(
      (entries) => {
        // Trouver l'entrée la plus visible
        const visibleEntry = entries.find(entry => entry.isIntersecting);
        if (visibleEntry) {
          const categoryId = visibleEntry.target.getAttribute('data-category-id');
          if (categoryId) {
            setActiveCategory(categoryId);
          }
        }
      },
      { threshold: 0.6 } // Observer quand au moins 60% de l'élément est visible
    );

    // Observer tous les boutons existants
    buttonRefs.current.forEach((button) => {
      observerRef.current?.observe(button);
    });

    return () => {
      // Nettoyer l'observateur
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  if (isLoading || isLoadingCategories) {
    return (
      <>
        <PublicHeader />
        <div className="min-h-screen bg-[#111216] flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
        </div>
      </>
    );
  }

  if (!award) {
    return (
      <>
        <PublicHeader />
        <div className="min-h-screen bg-[#111216] text-white flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4">Award non trouvé</h1>
            <button
              onClick={() => navigate('/awards')}
              className="text-yellow-400 hover:text-yellow-300"
            >
              Retour aux Awards
            </button>
          </div>
        </div>
      </>
    );
  }

  const activeContest = categories?.find(cat => cat.id === activeCategory)?.contest;

  return (
    <div className="min-h-screen bg-[#111216] text-white">
      <GoldSparkles />
      <PublicHeader />
      
      {/* SEO Meta Tags */}
      <SEOMetaTags
        title={award ? `${award.name} - Cérémonie de Récompense | VotAfrica` : 'Cérémonie de Récompense | VotAfrica'}
        description={award ? `Découvrez les nominés et votez pour vos favoris lors de la cérémonie ${award.name}. Participez à la célébration des talents africains sur VotAfrica.` : 'Découvrez les cérémonies de récompense et votez pour vos favoris. Célébrez les talents africains sur VotAfrica.'}
        image={award?.banner_url || award?.logo_url || 'https://votafrica.com/awards-banner.jpg'}
        url={`https://votafrica.com/awards/${slug}`}
      />
      
      {/* Composant VoteModal global qui sera affiché lorsque le store voteModalStore l'active */}
      <VoteModal />
      
      {/* Bannière époustouflante avec animations et effets luxueux */}
      <div className="relative min-h-[70vh] md:min-h-[90vh] overflow-hidden">
        {/* Fond avec image et effet de parallaxe */}
        <div 
          className="absolute inset-0 scale-110"
          style={{
            backgroundImage: `url('${award.banner_url || award.logo_url}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.5,
            animation: 'subtle-zoom 30s ease-in-out infinite alternate'
          }}
        />
        
        {/* Overlay avec dégradé et texture */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/70 to-[#111216]" />
        
        {/* Effet de lumière radiale */}
        <div 
          className="absolute inset-0 opacity-60"
          style={{
            background: 'radial-gradient(circle at 50% 40%, rgba(234,179,8,0.4) 0%, transparent 60%)',
            animation: 'pulse-glow 8s ease-in-out infinite'
          }}
        />
        
        {/* Particules dorées flottantes */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(40)].map((_, i) => {
            const size = 1 + Math.random() * 3;
            const duration = 15 + Math.random() * 20;
            const delay = Math.random() * 10;
            return (
              <div 
                key={i}
                className="absolute rounded-full"
                style={{
                  width: `${size}px`,
                  height: `${size}px`,
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  background: `rgba(${234 + Math.random() * 20}, ${150 + Math.random() * 50}, ${8 + Math.random() * 20}, ${0.6 + Math.random() * 0.4})`,
                  boxShadow: `0 0 ${3 + Math.random() * 5}px rgba(234,179,8,0.8)`,
                  animation: `float-particle ${duration}s ease-in-out infinite ${delay}s, twinkle ${2 + Math.random() * 4}s ease-in-out infinite ${Math.random() * 5}s`
                }}
              />
            );
          })}
        </div>
        
        {/* Rayons de lumière animés */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              background: 'conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(234,179,8,0.8) 20deg, transparent 40deg, transparent 60deg, rgba(234,179,8,0.8) 80deg, transparent 100deg, transparent 120deg, rgba(234,179,8,0.8) 140deg, transparent 160deg, transparent 180deg, rgba(234,179,8,0.8) 200deg, transparent 220deg, transparent 240deg, rgba(234,179,8,0.8) 260deg, transparent 280deg, transparent 300deg, rgba(234,179,8,0.8) 320deg, transparent 340deg)',
              animation: 'rotate-rays 60s linear infinite'
            }}
          />
        </div>
        
        {/* Motif géométrique en forme de diamant avec animation */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          {/* Diamant central animé */}
          <div 
            className="absolute w-[60%] h-[60%] opacity-40"
            style={{
              transform: 'rotate(45deg)',
              background: 'linear-gradient(135deg, rgba(234,179,8,0.05) 0%, rgba(234,179,8,0.3) 50%, rgba(234,179,8,0.05) 100%)',
              border: '1px solid rgba(234,179,8,0.5)',
              boxShadow: '0 0 60px rgba(234,179,8,0.3), inset 0 0 40px rgba(255,255,255,0.1)',
              animation: 'diamond-pulse 8s ease-in-out infinite'
            }}
          />
          
          {/* Cadres décoratifs concentriques */}
          {[75, 85, 95].map((size, i) => (
            <div 
              key={i}
              className="absolute opacity-30"
              style={{
                width: `${size}%`,
                height: `${size}%`,
                transform: 'rotate(45deg)',
                border: '1px solid rgba(234,179,8,0.3)',
                boxShadow: '0 0 30px rgba(234,179,8,0.15)',
                animation: `diamond-pulse ${8 + i}s ease-in-out infinite ${i * 0.5}s`
              }}
            />
          ))}
          
          {/* Lignes décoratives avec animation */}
          {[...Array(8)].map((_, i) => (
            <div 
              key={i}
              className="absolute h-[200%] w-[1px] opacity-30"
              style={{
                background: 'linear-gradient(to bottom, transparent, rgba(234,179,8,0.7) 50%, transparent)',
                transform: `rotate(${i * 22.5}deg)`,
                animation: 'line-pulse 4s ease-in-out infinite alternate'
              }}
            />
          ))}
          
          {/* Points lumineux aux intersections */}
          {[...Array(16)].map((_, i) => {
            const angle = i * 22.5;
            const distance = 250 + Math.sin(i * 0.5) * 50;
            return (
              <div 
                key={i}
                className="absolute w-2 h-2 rounded-full bg-yellow-300 opacity-80"
                style={{
                  transform: `rotate(0deg) translate(${Math.cos(angle * Math.PI / 180) * distance}px, ${Math.sin(angle * Math.PI / 180) * distance}px)`,
                  boxShadow: '0 0 15px 5px rgba(234,179,8,0.6)',
                  animation: `point-pulse ${3 + Math.random() * 2}s ease-in-out infinite ${Math.random() * 2}s`
                }}
              />
            );
          })}
        </div>
        
        {/* Ruban doré avec effet 3D et animation */}
        <div className="absolute top-8 left-0 right-0 overflow-hidden pointer-events-none">
          <div className="relative max-w-5xl mx-auto">
            <div 
              className="absolute -left-8 -right-8 h-16 transform -skew-y-1 bg-gradient-to-r from-amber-800/60 via-yellow-500/60 to-amber-800/60 border-t border-b border-yellow-300/50"
              style={{ 
                boxShadow: '0 0 30px rgba(234,179,8,0.4), inset 0 0 20px rgba(255,255,255,0.1)',
                animation: 'ribbon-shine 8s ease-in-out infinite'
              }}
            />
            <div className="absolute left-0 right-0 h-14 flex items-center justify-center">
              <p className="text-xs sm:text-sm md:text-base text-yellow-100 font-medium tracking-widest uppercase" style={{ textShadow: '0 0 10px rgba(234,179,8,0.8)' }}>
                Célébration d'Excellence • {new Date().getFullYear()} • Cérémonie Officielle
              </p>
            </div>
          </div>
        </div>
        
        <div className="relative z-20 h-full container mx-auto px-4 flex flex-col">
          {/* Espace minimal pour maximiser le décalage vers le bas */}
          <div className="pt-2 sm:pt-4 md:pt-0"></div>
          <div className="flex-1 flex flex-col justify-center">
            <motion.div 
              className="max-w-4xl mx-auto text-center space-y-2 sm:space-y-8 relative p-3 sm:p-12"
              {...fadeIn}
            >
              {/* Fond spécial avec effet fondant */}
              <div className="absolute inset-0 blur-2xl">
                <div className="absolute inset-0 bg-gradient-radial from-yellow-400/20 via-black/40 to-transparent" />
                <div className="absolute inset-0 bg-gradient-conic from-black/50 via-yellow-400/10 to-black/50" />
              </div>
                
              {/* Effet de brillance */}
              <div 
                className="absolute inset-0 opacity-30"
                style={{
                  background: 'radial-gradient(ellipse at 50% 50%, rgba(255,255,255,0.2), transparent 70%)',
                  filter: 'blur(20px)'
                }}
              />  
              
              
              {/* Ajout d'un style global pour l'animation */}
              <style dangerouslySetInnerHTML={{ __html: `
                @keyframes spin {
                  from { transform: rotate(0deg); }
                  to { transform: rotate(360deg); }
                }
              `}} />

              {/* Contenu - espacement ajusté pour mobile avec titre décalé encore plus vers le bas */}
              <div className="relative z-10 py-4 sm:py-12 px-2 sm:px-8 pt-24 sm:pt-32">
                {/* Titre principal avec effet spécial - marges ajustées pour mobile */}
                <div className="relative mt-1 sm:mt-4 mb-4 sm:mb-12 px-2 sm:px-4">
                  {/* Titre avec effet de texte luxueux */}
                  {/* Titre avec bordures brillantes */}
                  <div className="relative">
                    {/* Effet de bordure brillante - couche 1 - taille de texte réduite pour mobile */}
                    <h1 className="
                      absolute -left-[2px] -top-[2px]
                      text-3xl sm:text-7xl md:text-8xl 
                      font-playfair font-bold
                      text-white/5
                      leading-tight
                      text-center
                      tracking-wide
                      py-2 sm:py-4
                      w-full
                      blur-[2px]
                    ">
                      {award.name}
                    </h1>
                    
                    {/* Effet de bordure brillante - couche 2 - taille de texte réduite pour mobile */}
                    <h1 className="
                      absolute -left-[1px] -top-[1px]
                      text-3xl sm:text-7xl md:text-8xl 
                      font-playfair font-bold
                      text-white/10
                      leading-tight
                      text-center
                      tracking-wide
                      py-2 sm:py-4
                      w-full
                      blur-[1px]
                    ">
                      {award.name}
                    </h1>
                    
                    {/* Effet de bordure dorée - taille de texte réduite pour mobile */}
                    <h1 className="
                      absolute
                      text-3xl sm:text-7xl md:text-8xl 
                      font-playfair font-bold
                      text-transparent
                      leading-tight
                      text-center
                      tracking-wide
                      py-2 sm:py-4
                      w-full
                      [text-shadow:0_0_1px_rgba(234,179,8,0.8),0_0_2px_rgba(234,179,8,0.4)]
                    ">
                      {award.name}
                    </h1>
                    
                    {/* Texte principal avec dégradé - taille de texte réduite pour mobile */}
                    <h1 className="
                      relative z-10
                      text-3xl sm:text-7xl md:text-8xl 
                      font-playfair font-bold
                      text-transparent bg-clip-text
                      leading-tight
                      text-center
                      tracking-wide
                      py-2 sm:py-4
                    "
                    style={{
                      background: 'linear-gradient(to bottom, #f7e08c, #eab308 45%, #b45309 65%, #eab308 85%, #f7e08c)',
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text'
                    }}
                    >
                      {award.name}
                    </h1>
                  </div>
                  
                  {/* Ligne décorative sous le texte */}
                  <div className="relative w-full max-w-3xl mx-auto h-[2px] mt-4 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-400/80 to-transparent"></div>
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-800/30 via-yellow-400/50 to-amber-800/30 animate-shimmer"></div>
                  </div>
                </div>



                {/* Statistiques élégantes sans cadre - disposition flex ajustée pour mobile */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-16 mt-4 sm:mt-6 mb-2">
                  {/* Ligne décorative visible uniquement sur mobile */}
                  <div className="h-[1px] w-32 bg-gradient-to-r from-transparent via-yellow-400/40 to-transparent mb-3 sm:hidden"></div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Trophy className="w-4 sm:w-6 h-4 sm:h-6 text-yellow-400 drop-shadow-lg" />
                    <span className="font-playfair tracking-wider text-xs sm:text-base text-white/90 drop-shadow font-semibold">
                      {award.award_categories.length} catégories
                    </span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Calendar className="w-4 sm:w-6 h-4 sm:h-6 text-yellow-400 drop-shadow-lg" />
                    <span className="font-playfair tracking-wider text-xs sm:text-base text-white/90 drop-shadow font-semibold">
                      {new Date(award.start_date).toLocaleDateString('fr-FR', {
                        year: 'numeric',
                        month: 'long'
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <MapPin className="w-4 sm:w-6 h-4 sm:h-6 text-yellow-400 drop-shadow-lg" />
                    <span className="font-playfair tracking-wider text-xs sm:text-base text-white/90 drop-shadow font-semibold">
                      {award.location}
                    </span>
                  </div>
                  
                  {/* Ligne décorative visible uniquement sur mobile */}
                  <div className="h-[1px] w-32 bg-gradient-to-r from-transparent via-yellow-400/40 to-transparent mt-3 sm:hidden"></div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      

      {/* Bouton pour afficher/masquer les catégories - espacement réduit sur mobile */}
      <div className="flex justify-center py-2 sm:py-4 gap-3 sm:gap-4 -mt-2 sm:mt-0">
        <button 
          onClick={() => setShowCategories(!showCategories)}
          className="px-6 py-3 bg-gradient-to-r from-yellow-900/20 to-yellow-700/20 hover:from-yellow-900/30 hover:to-yellow-700/30 text-yellow-400 rounded-full border border-yellow-400/30 shadow-md hover:shadow-yellow-400/5 transition-all duration-300 flex items-center gap-3 backdrop-blur-sm group"
        >
          <span className={`transform transition-transform duration-300 ${showCategories ? 'rotate-180' : ''}`}>
            {showCategories ? '▲' : '▼'}
          </span>
          <span className="font-medium tracking-wide">
            {showCategories ? 'Masquer les catégories' : 'Afficher les catégories'}
          </span>
          <span className={`transform transition-transform duration-300 ${showCategories ? 'rotate-180' : ''}`}>
            {showCategories ? '▲' : '▼'}
          </span>
        </button>
        
        <button 
          onClick={selectRandomCategory}
          className="px-6 py-3 bg-gradient-to-r from-yellow-900/20 to-yellow-700/20 hover:from-yellow-900/30 hover:to-yellow-700/30 text-yellow-400 rounded-full border border-yellow-400/30 shadow-md hover:shadow-yellow-400/5 transition-all duration-300 flex items-center gap-3 backdrop-blur-sm group"
          aria-label="Sélectionner une catégorie aléatoire"
        >
          <span className="transform rotate-0 group-hover:rotate-180 transition-transform duration-500">⟳</span>
          <span className="font-medium tracking-wide">Catégorie aléatoire</span>
        </button>
      </div>
      
      {/* Navigation des catégories - conditionnellement affichée */}
      <div 
        className={`relative z-10 bg-gradient-to-b from-black/90 to-[#111216]/90 backdrop-blur-xl border-t border-b border-yellow-400/20 shadow-[0_4px_30px_rgba(234,179,8,0.1)] transition-all duration-500 overflow-hidden ${showCategories ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0 border-t-0 border-b-0'}`}
      >
        {/* Ligne décorative supérieure */}
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-yellow-400/40 to-transparent"></div>
        <div className="container mx-auto px-4">
          {/* Titre accrocheur pour les catégories */}
          <div className="text-center mb-4 mt-6">
            <p className="text-yellow-400/80 text-sm sm:text-base italic max-w-2xl mx-auto">
              "Découvrez les trésors cachés de l'innovation africaine à travers ces catégories prestigieuses"
            </p>
            <div className="flex justify-center mt-2">
              <div className="h-[1px] w-24 bg-gradient-to-r from-transparent via-yellow-400/60 to-transparent"></div>
            </div>
          </div>

          
          {/* Ligne décorative */}
          <div className="mb-2">
            <div className="flex items-center justify-center gap-2">
              <div className="h-[1px] w-12 bg-gradient-to-r from-transparent to-yellow-400/30"></div>
              <div className="text-yellow-400/80 text-xs">✦</div>
              <div className="h-[1px] w-12 bg-gradient-to-r from-transparent to-yellow-400/30"></div>
            </div>
          </div>
          
          {/* Conteneur principal */}
          <div className="flex items-center gap-4">

            {/* Conteneur scrollable avec ombre de fondu */}
            <div className="relative flex-1 overflow-hidden">
              {/* Liste scrollable des catégories */}
              <div 
                id="categories-scroll"
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 gap-2 sm:gap-3 py-4 px-2 sm:px-4 relative transition-all duration-300"
                tabIndex={0}
              >
                {/* Ligne de progression */}
                <div className="absolute bottom-0 left-8 right-8 h-[2px] bg-white/10 hidden sm:block"></div>
                
                {/* Utiliser les catégories triées par date (déjà triées dans la requête) */}
                {categories?.map((category, index) => (
                  <button
                    key={category.id}
                    ref={(el) => {
                      if (el) {
                        buttonRefs.current.set(category.id, el);
                      } else {
                        buttonRefs.current.delete(category.id);
                      }
                    }}
                    data-category-id={category.id}
                    className={`
                      relative group
                      px-2 py-3 m-0.5 sm:m-1 min-w-0 w-full flex-grow-0 flex-shrink-0 basis-auto text-xs sm:text-sm border border-yellow-400/10 bg-black/20 rounded-lg hover:bg-black/30 transition-colors duration-200
                      font-playfair font-bold tracking-wider uppercase
                      transition-all duration-300 overflow-hidden
                      flex flex-col items-center justify-center
                      ${activeCategory === category.id
                        ? 'text-yellow-400'
                        : 'text-white/70 hover:text-white'
                      }
                    `}
                    style={{
                      fontFamily: 'Playfair Display, serif',
                      letterSpacing: '0.05em'
                    }}
                    onClick={() => {
                      setActiveCategory(category.id);
                    }}
                  >
                    <div className="absolute top-1 right-1 flex items-center justify-center w-5 h-5 rounded-full bg-black/40 border border-yellow-400/20">
                      <span className="text-[10px] font-bold text-yellow-400">{index + 1}</span>
                    </div>
                    
                    {/* Effet de brillance au survol */}
                    <div className={`
                      absolute inset-0 opacity-0 group-hover:opacity-100
                      bg-gradient-to-r from-transparent via-white/5 to-transparent
                      translate-x-[-100%] group-hover:translate-x-[100%]
                      transition-all duration-700
                    `} />

                    {/* Texte avec ellipsis et mise en valeur */}
                    <div className="relative flex flex-col items-center">
                      {/* Icône décorative */}

                      
                      {/* Titre avec effet de brillance */}
                      <span className="relative block font-bold text-sm sm:text-base whitespace-normal text-center px-2 sm:px-4 leading-tight" style={{ 
                        fontFamily: 'Playfair Display, serif', 
                        letterSpacing: '0.05em', 
                        color: activeCategory === category.id ? '#facc15' : 'inherit'
                      }}>
                        {category.name}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>


          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="container mx-auto px-4 py-16">
        {activeContest && (
          <motion.div 
            key={activeCategory}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            {/* En-tête de la catégorie */}
            <div className="flex flex-col items-center mx-auto mb-8 sm:mb-16 max-w-4xl">
              <div className="relative w-full mb-6">
                {/* Ligne décorative */}
                <div className="absolute -left-8 top-0 bottom-0 w-[2px]">
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-yellow-400/50 to-transparent" />
                </div>

                {/* Titre avec effet spécial */}
                <div className="relative">
                  <h2 className="
                    text-3xl sm:text-5xl font-playfair font-semibold mb-4 sm:mb-6
                    text-yellow-400
                    tracking-wide
                    px-2 sm:px-0
                  ">
                    {award.award_categories?.find((cat: any) => cat.id === activeCategory)?.name}
                  </h2>

                  {/* Ornement sous le titre */}
                  <div className="absolute left-1/2 bottom-4 -translate-x-1/2 w-1/2 h-[1px] bg-gradient-to-r from-transparent via-yellow-400 to-transparent"></div>
                </div>

                {/* Description avec style élégant */}
                <div className="relative mt-8 pl-6">
                  {/* Ornement vertical */}
                  <div className="
                    absolute left-0 top-2 bottom-2
                    w-[1px] bg-gradient-to-b from-yellow-400/20 to-transparent
                  " />
                  
                  <p className="
                    text-base sm:text-lg font-playfair text-white/80
                    leading-relaxed tracking-wide
                    max-w-2xl
                  ">
                    {activeContest.description}
                  </p>

                  {/* Effet de brillance */}
                  <div className="
                    absolute -inset-2
                    bg-gradient-to-r from-yellow-400/0 via-yellow-400/5 to-yellow-400/0
                    rounded-lg opacity-0 group-hover:opacity-100
                    transition-opacity duration-700
                    pointer-events-none
                  " />
                </div>
              </div>


            </div>

            {/* Blocs de candidats */}
            <AwardContestantBlocks contestId={activeContest.id} />
          </motion.div>
        )}
        
        {/* Section NOS PARTENAIRES - Design luxueux et interactif */}
        {award.partner_logos && award.partner_logos.length > 0 && (
          <div className="mt-24 mb-16 relative overflow-hidden">
            {/* Arrière-plan décoratif */}
            <div className="absolute inset-0 bg-black/40 -z-10 overflow-hidden">
              {/* Motif géométrique doré */}
              <div 
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage: 'radial-gradient(circle, rgba(234,179,8,0.8) 1px, transparent 1px), radial-gradient(circle, rgba(234,179,8,0.4) 2px, transparent 2px)',
                  backgroundSize: '30px 30px, 90px 90px',
                  backgroundPosition: '0 0, 15px 15px'
                }}
              />
              
              {/* Lignes diagonales dorées */}
              <div className="absolute inset-0 opacity-10" style={{ background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(234,179,8,0.5) 10px, rgba(234,179,8,0.5) 11px)' }} />
            </div>
            
            {/* Titre élégant avec ornements */}
            <div className="relative mb-12 text-center">
              <div className="flex items-center justify-center mb-3">
                <div className="h-[1px] w-16 sm:w-32 bg-gradient-to-r from-transparent via-yellow-400/70 to-transparent"></div>
                <div className="mx-4 text-yellow-400/70 text-xl">✦</div>
                <div className="h-[1px] w-16 sm:w-32 bg-gradient-to-r from-transparent via-yellow-400/70 to-transparent"></div>
              </div>
              
              <div className="relative">
                <p className="text-xs text-yellow-400/60 uppercase tracking-[0.2em] font-sans mb-2">Ils nous font confiance</p>
                <h2 className="inline-block text-3xl sm:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-300 via-yellow-500 to-amber-300 font-playfair tracking-wide">
                  NOS PARTENAIRES
                  <span className="block text-xl font-medium text-amber-300/80 mt-1">& SPONSORS OFFICIELS</span>
                </h2>
              </div>
              
              <div className="flex items-center justify-center mt-3">
                <div className="h-[1px] w-16 sm:w-32 bg-gradient-to-r from-transparent via-yellow-400/70 to-transparent"></div>
                <div className="mx-4 text-yellow-400/70 text-xl">✦</div>
                <div className="h-[1px] w-16 sm:w-32 bg-gradient-to-r from-transparent via-yellow-400/70 to-transparent"></div>
              </div>
            </div>
            
            {/* Carrousel de logos avec effet 3D */}
            <div className="relative max-w-6xl mx-auto px-4">
              {/* Effet de lumière radiale */}
              <div 
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full -z-10 opacity-30"
                style={{
                  background: 'radial-gradient(circle, rgba(234,179,8,0.3) 0%, transparent 70%)',
                  filter: 'blur(40px)'
                }}
              />
              
              {/* Conteneur de logos */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 px-4">
                {award.partner_logos.map((logo: string, index: number) => (
                  <div 
                    key={index} 
                    className="bg-gradient-to-br from-amber-700/20 via-yellow-500/10 to-amber-700/20 backdrop-blur-md rounded-2xl p-6 flex items-center justify-center transform transition-all duration-300 hover:scale-105 hover:shadow-[0_0_15px_rgba(245,158,11,0.3)] border border-amber-500/30 hover:border-amber-400/50"
                    style={{
                      boxShadow: '0 4px 20px rgba(245, 158, 11, 0.15)'
                    }}
                  >
                    <img 
                      src={logo} 
                      alt={`Partenaire ${index + 1}`} 
                      className="max-w-full h-auto filter contrast-125 brightness-110 transition-all duration-300" 
                      style={{
                        filter: 'sepia(30%) hue-rotate(5deg) saturate(150%) brightness(110%) contrast(110%)'
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Texte de remerciement */}
            <div className="text-center mt-12 text-amber-300/60 italic text-sm max-w-2xl mx-auto px-4">
              Nous remercions chaleureusement tous nos partenaires et sponsors pour leur soutien inestimable dans la réalisation de cet événement prestigieux.
            </div>
            
            {/* Styles pour les animations */}
            <style dangerouslySetInnerHTML={{ __html: `
              @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
              
              @keyframes twinkle {
                0%, 100% { opacity: 0.2; transform: scale(0.8); }
                50% { opacity: 1; transform: scale(1.2); }
              }
              
              @keyframes slide-diagonal {
                0% { transform: translateX(-20%) translateY(-20%); }
                100% { transform: translateX(20%) translateY(20%); }
              }
              
              @keyframes subtle-zoom {
                0% { transform: scale(1.1); }
                100% { transform: scale(1.15); }
              }
              
              @keyframes pulse-glow {
                0%, 100% { opacity: 0.4; }
                50% { opacity: 0.7; }
              }
              
              @keyframes float-particle {
                0%, 100% { transform: translateY(0) translateX(0); }
                25% { transform: translateY(-100px) translateX(50px); }
                50% { transform: translateY(-50px) translateX(100px); }
                75% { transform: translateY(100px) translateX(-50px); }
              }
              
              @keyframes rotate-rays {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              
              @keyframes diamond-pulse {
                0%, 100% { opacity: 0.3; transform: rotate(45deg) scale(1); }
                50% { opacity: 0.5; transform: rotate(45deg) scale(1.05); }
              }
              
              @keyframes line-pulse {
                0% { opacity: 0.2; }
                100% { opacity: 0.4; }
              }
              
              @keyframes point-pulse {
                0%, 100% { opacity: 0.7; transform: scale(0.8); }
                50% { opacity: 1; transform: scale(1.2); }
              }
              
              @keyframes ribbon-shine {
                0%, 100% { background-position: -100% 0; }
                50% { background-position: 200% 0; }
              }
            `}} />
          </div>
        )}
      </div>

      <PublicFooter />
    </div>
  );
};
